import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from 'antd';
import { BaseAPIClient, type APIError } from '@/services/api';
import { useSessionRoutes } from './useSessionRoutes';

// One key, so simultaneous 410s replace each other's notice instead of stacking.
const REVOKED_NOTIFICATION_KEY = 'package-revoked';

const REVOKED_MESSAGE = 'This package was removed from your account';

/**
 * Nav state the kick-out lands on Sessions with. Sessions refetches on it,
 * since a kick-out from the hub itself doesn't remount the page.
 */
export interface PackageRevokedNavState {
  packageRevoked: true;
}

/** 410 is only sent for a revoked package (InterviewTemplateRevokedError in the API). */
export const isRevokedError = (err: unknown): boolean =>
  BaseAPIClient.isApiError(err) && err.status === 410;

/** The error a 410 produces, for client-side revoke checks (see useEnterAssessment). */
export const revokedError = (): APIError => ({ message: REVOKED_MESSAGE, status: 410 });

/**
 * Returns `handleIfRevoked(err)`: for a revoked-package error, notifies the
 * candidate, sends them to Sessions and returns true so the caller skips its
 * own error toast. Returns false otherwise. Navigating away also ends a live
 * voice call by unmounting VoiceAnswerPanel.
 */
export const useRevokedKickOut = () => {
  const navigate = useNavigate();
  const sessionRoutes = useSessionRoutes();
  const { notification } = App.useApp();

  return useCallback(
    (err: unknown): boolean => {
      if (!isRevokedError(err)) return false;
      notification.info({ key: REVOKED_NOTIFICATION_KEY, message: REVOKED_MESSAGE });
      const state: PackageRevokedNavState = { packageRevoked: true };
      navigate(sessionRoutes.hub, { replace: true, state });
      return true;
    },
    [navigate, notification, sessionRoutes]
  );
};

export default useRevokedKickOut;
